import React from "react";
import { useImmer } from "use-immer";

import { IgIconColors } from "@intelligems/ig-design-tokens/constants";
import { ButtonGroup, ButtonProps, Menu, MenuItem } from "@mui/material";
import Grid from "@mui/material/Grid";

import { IgButton } from "./IgButton";
import { IgIconComponent } from "./IgIconComponent";
import { IgTooltip } from "./IgTooltip";

interface IgSplitButtonProps extends ButtonProps {
  /**
   * Hover copy for the MAIN button — why it is unavailable, usually. It never
   * reaches the caret, whose own reasons live on its menu items, and it is
   * withheld while the menu is open so it cannot sit over the menu.
   *
   * Prefer this to wrapping the whole split button in a tooltip: a wrapper
   * binds its handlers (and, under React 19, its ref) to whatever the
   * component spreads them on, and the caret must not be that.
   */
  tooltip?: React.ReactNode;
  onMenuOpenChange?: (open: boolean) => void;
  /**
   * Whether the caret is hovered or focused. Cleared when the menu closes,
   * whichever way it closed — a keyboard close leaves the caret focused, and
   * a listener that read "still active" then would never see it settle.
   */
  onMenuTriggerActiveChange?: (active: boolean) => void;
  menuOptions: {
    key: string;
    disabledMessage?: string;
    action: () => void;
    hidden?: boolean;
  }[];
  /**
   * `36` (the default: a 36px control with 12px inline padding and a 36×36
   * caret) or `40`: a 40px control whose primary segment sits on a 100px minimum with
   * 16px inline padding, a 40×40 caret, a 6px radius and a light divider.
   */
  height?: 36 | 40;
  /**
   * Which edge of the button the menu lines up with. `"end"` hangs it under
   * the caret, right-aligned — the menu belongs to the caret, not the label.
   * Defaults to `"start"`, the left edge, as before.
   */
  menuAlignment?: "start" | "end";
}

/**
 * A primary action with the rest of its family behind a caret.
 *
 * `menuOptions[0]` is the primary: its `key` is the button label and it is
 * conventionally `hidden`, which drops it from the menu entirely rather than
 * repeating the button as a second, independently-enabled control. `disabled`
 * applies to the primary action alone — the caret is a separate control with
 * its own reasons to be unavailable, and greying it out with the primary hides
 * the alternatives exactly when the reader is looking for one (a rule with
 * nothing left to save is precisely when "Run tests" or "Unpublish" is what
 * they want). When there is nothing behind the caret, no caret is drawn.
 *
 * Every other button prop — including whatever a wrapping tooltip injects —
 * lands on the MAIN button only. The caret takes the shared `color` and
 * nothing else from the caller.
 */
export const IgSplitButton = (props: IgSplitButtonProps) => {
  const {
    menuOptions,
    startIcon,
    disabled,
    tooltip,
    onMenuOpenChange,
    onMenuTriggerActiveChange,
    height = 36,
    menuAlignment = "start",
    ...rest
  } = props;
  const menuHorizontal = menuAlignment === "end" ? "right" : "left";
  const isTall = height === 40;
  const [open, setOpen] = useImmer(false);
  const anchorRef = React.useRef<HTMLDivElement>(null);
  const [anchorEl, setAnchorEl] = useImmer<null | HTMLElement>(null);

  const [selectedIndex, setSelectedIndex] = useImmer(0);

  const closeMenu = () => {
    setOpen(false);
    onMenuOpenChange?.(false);
    onMenuTriggerActiveChange?.(false);
  };

  const handleClick = () => {
    menuOptions[0].action();
    closeMenu();
  };

  const handleMenuItemClick = (
    event: React.MouseEvent<HTMLLIElement, MouseEvent>,
    index: number
  ) => {
    setSelectedIndex(index);
    closeMenu();
    menuOptions[index].action();
  };

  const handleToggle = () => {
    const nextOpen = !open;
    if (nextOpen) {
      setOpen(true);
      onMenuOpenChange?.(true);
      setAnchorEl(anchorRef.current);
    } else {
      closeMenu();
    }
  };

  const handleClose = (event: Event) => {
    if (
      anchorRef.current &&
      anchorRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }

    closeMenu();
    setAnchorEl(null);
  };

  const visibleOptions = menuOptions.filter((option) => !option.hidden);
  const isMenuDisabled = visibleOptions.every((option) =>
    Boolean(option.disabledMessage)
  );

  const mainButton = (
    <IgButton
      {...rest}
      disabled={disabled}
      startIcon={startIcon}
      size={"small"}
      sx={
        isTall
          ? { height: "40px", minWidth: "100px", px: 4, borderRadius: "6px" }
          : { height: "36px", px: 3 }
      }
      variant={"contained"}
      onClick={handleClick}
    >
      {menuOptions[0].key}
    </IgButton>
  );
  // Withheld while the menu is open: a tooltip that opened over the menu
  // would describe the button the reader has just moved on from.
  const primaryButton =
    tooltip === undefined ? (
      mainButton
    ) : (
      <IgTooltip title={open ? "" : tooltip}>{mainButton}</IgTooltip>
    );

  // Nothing behind the caret means there is no split to make — a lone primary
  // action is a plain button, and a caret onto an empty menu is a dead end.
  if (visibleOptions.length === 0) {
    return <Grid sx={{ padding: 0 }}>{primaryButton}</Grid>;
  }

  return (
    <Grid sx={{ padding: 0 }}>
      <ButtonGroup
        disableElevation={true}
        variant="contained"
        ref={anchorRef}
        sx={
          isTall
            ? {
                borderRadius: "6px",
                // ButtonGroup floors every grouped child with
                // `.root .MuiButtonGroup-grouped { min-width: 40px }`, two
                // classes deep, which outranks the primary's own one-class
                // `sx`. Set at the same depth here, later in the sheet, so the
                // 100px minimum holds for a short label ("Saved").
                "& .MuiButtonGroup-firstButton": { minWidth: "100px" },
                // One light divider whether or not the primary is disabled.
                "& .MuiButtonGroup-firstButton, & .MuiButtonGroup-firstButton.Mui-disabled":
                  { borderRight: `1px solid ${IgIconColors.dark}` },
              }
            : // The group's own 40px `grouped` minimum would widen the caret.
              { "& .MuiButtonGroup-lastButton": { minWidth: "36px" } }
        }
      >
        {primaryButton}
        <IgButton
          color={rest.color}
          disabled={isMenuDisabled}
          aria-expanded={open}
          aria-haspopup="menu"
          aria-label="More actions"
          sx={
            isTall
              ? {
                  height: "40px",
                  width: "40px",
                  minWidth: "40px",
                  padding: 0,
                  borderRadius: "6px",
                }
              : {
                  height: "36px",
                  width: "36px",
                  minWidth: "36px",
                  padding: 0,
                }
          }
          variant={"contained"}
          size="small"
          onClick={handleToggle}
          onMouseEnter={() => onMenuTriggerActiveChange?.(true)}
          onMouseLeave={(event) =>
            onMenuTriggerActiveChange?.(
              document.activeElement === event.currentTarget
            )
          }
          onFocus={() => onMenuTriggerActiveChange?.(true)}
          onBlur={(event) =>
            onMenuTriggerActiveChange?.(event.currentTarget.matches(":hover"))
          }
        >
          <IgIconComponent color={"white"} />
        </IgButton>
      </ButtonGroup>
      <Menu
        disableScrollLock={true}
        open={open}
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: "bottom", horizontal: menuHorizontal }}
        transformOrigin={{ vertical: "top", horizontal: menuHorizontal }}
        onClose={handleClose}
      >
        {/* Hidden options are NOT rendered: an option drawn with
            `display: none` is invisible but still a live control — clickable
            by script, and a second Save that ignores the primary's disabled
            state. The index passed to the click handler still indexes
            `menuOptions`, so the primary keeps its conventional slot 0. */}
        {menuOptions.map((option, index) =>
          option.hidden ? null : (
            <IgTooltip key={option.key} title={option.disabledMessage}>
              <MenuItem
                disabled={!!option.disabledMessage}
                selected={index === selectedIndex}
                onClick={(event) => handleMenuItemClick(event, index)}
              >
                {option.key}
              </MenuItem>
            </IgTooltip>
          )
        )}
      </Menu>
    </Grid>
  );
};
